import React, { useState, createContext, useContext, useMemo, useId, useEffect, useRef } from "react";
import type { ReactNode } from "react";
import clsx from "clsx";
import Button from "../Button/Button"; 

/**
 * Tab - Componente de navegação por abas acessível, internacionalizável e com suporte a tema (Tailwind).
 *
 * Permite organizar conteúdo em diferentes painéis, acessíveis por navegação por teclado e mouse.
 * Suporta temas claro/escuro via prop `theme`, tooltips acessíveis, mensagens customizadas via contexto, desabilitar abas e integração com testes automatizados.
 * Todo o estilo é feito com Tailwind CSS, garantindo responsividade e consistência visual.
 *
 * Principais recursos:
 * - Navegação por teclado (setas, Home/End), pulando abas desabilitadas
 * - Tooltips acessíveis nos itens de aba
 * - Suporte a temas "light" e "dark" via prop
 * - Mensagens internacionalizáveis via contexto ou prop
 * - Props para aria-label, aria-describedby, data-testid
 * - Abas desabilitáveis
 * - Totalmente acessível (roles, aria-*)
 * - Integração com testes automatizados (data-testid)
 * - Contexto para internacionalização de mensagens
 * - Suporte a múltiplos painéis e controle de estado externo (controlado e não controlado)
 * - Estilização 100% Tailwind, sem CSS externo
 * - Responsivo por padrão
 * - Aparência e comportamento inspirados em Material Design e Fluent UI (linha animada, foco visual, cores)
 * - Renderização programática: aceita array de tabs e render prop para Tab.Item
 * - Todos os painéis são sempre renderizados (usando hidden), melhorando acessibilidade
 * - IDs de tab e painel sempre consistentes e únicos (usando useId)
 * - Subcomponentes otimizados com React.memo e handlers memoizados
 *
 * @component
 * @param {object} props
 * @param {ReactNode} [props.children] - Subcomponentes Tab.List, Tab.Item e Tab.Panel.
 * @param {TabData[]} [props.tabs] - Array de objetos para renderização programática das abas.
 * @param {(tab: TabData, index: number) => ReactNode} [props.renderTabItem] - Função para renderizar cada Tab.Item a partir do array tabs.
 * @param {number} [props.activeIndex] - Índice da aba ativa (modo controlado).
 * @param {(index: number) => void} [props.onChange] - Callback ao trocar de aba.
 * @param {number} [props.defaultIndex=0] - Índice inicial da aba ativa (modo não controlado).
 * @param {"light"|"dark"} [props.theme="light"] - Tema visual do componente (propagado para todos os subcomponentes).
 * @param {string} [props.className] - Classe CSS extra para customização.
 * @param {string} [props.data-testid] - Test id para testes automatizados.
 * @param {TabI18nContext} [props.i18nMessages] - Mensagens customizadas para internacionalização.
 * @returns {JSX.Element} Componente Tab renderizado.
 *
 * @example
 * // Uso básico com tema escuro e tooltips acessíveis
 * <Tab theme="dark" i18nMessages={{
 *   getTabLabel: (i, label) => label ?? `Aba ${i + 1}`,
 *   getTabTooltip: (i, tooltip) => tooltip ?? `Dica da aba ${i + 1}`
 * }}>
 *   <Tab.List>
 *     <Tab.Item index={0} label="Dados" tooltip="Preencha seus dados" />
 *     <Tab.Item index={1} label="Pagamento" tooltip="Escolha a forma de pagamento" />
 *     <Tab.Item index={2} label="Confirmação" tooltip="Revise e confirme" disabled />
 *   </Tab.List>
 *   <Tab.Panel index={0}>Conteúdo 1</Tab.Panel>
 *   <Tab.Panel index={1}>Conteúdo 2</Tab.Panel>
 *   <Tab.Panel index={2}>Conteúdo 3</Tab.Panel>
 * </Tab>
 *
 * @example
 * // Uso programático com array de tabs
 * <Tab
 *   tabs={[
 *     { label: "Aba 1", tooltip: "Dica 1" },
 *     { label: "Aba 2", tooltip: "Dica 2", disabled: true },
 *     { label: "Aba 3" }
 *   ]}
 *   renderTabItem={(tab, idx) => (
 *     <Tab.Item
 *       key={idx}
 *       index={idx}
 *       label={tab.label}
 *       tooltip={tab.tooltip}
 *       disabled={tab.disabled}
 *     />
 *   )}
 * >
 *   <Tab.Panel index={0}>Conteúdo 1</Tab.Panel>
 *   <Tab.Panel index={1}>Conteúdo 2</Tab.Panel>
 *   <Tab.Panel index={2}>Conteúdo 3</Tab.Panel>
 * </Tab>
 *
 * @see Tab.List
 * @see Tab.Item
 * @see Tab.Panel
 */

/**
 * Contexto para internacionalização de mensagens do componente Tab.
 * Permite customizar os textos de label e tooltip das abas.
 * @typedef {Object} TabI18nContext
 * @property {(index: number, label?: string) => string} [getTabLabel] - Função para gerar o label da aba.
 * @property {(index: number, tooltip?: string) => string} [getTabTooltip] - Função para gerar o tooltip da aba.
 */

/**
 * Propriedades do componente Tab.
 * @typedef {Object} TabProps
 * @property {ReactNode} children - Subcomponentes Tab.List, Tab.Item e Tab.Panel.
 * @property {number} [activeIndex] - Índice da aba ativa (modo controlado).
 * @property {(index: number) => void} [onChange] - Callback ao trocar de aba.
 * @property {number} [defaultIndex=0] - Índice inicial da aba ativa (modo não controlado).
 * @property {"light"|"dark"} [theme="light"] - Tema visual do componente.
 * @property {string} [className] - Classe CSS extra para customização.
 * @property {string} [data-testid] - Test id para testes automatizados.
 * @property {TabI18nContext} [i18nMessages] - Mensagens customizadas para internacionalização.
 */

/**
 * Propriedades do componente Tab.List.
 * @typedef {Object} TabListProps
 * @property {ReactNode} children - Elementos Tab.Item.
 * @property {string} [className] - Classe CSS extra.
 * @property {string} [data-testid] - Test id para testes.
 */

/**
 * Propriedades do componente Tab.Item.
 * @typedef {Object} TabItemProps
 * @property {ReactNode} children - Conteúdo da aba.
 * @property {number} index - Índice da aba.
 * @property {string} [label] - Label da aba (para acessibilidade).
 * @property {string} [tooltip] - Tooltip acessível da aba.
 * @property {boolean} [disabled=false] - Se a aba está desabilitada.
 * @property {string} [className] - Classe CSS extra.
 * @property {string} [data-testid] - Test id para testes.
 * @property {string} [aria-label] - Label acessível customizado.
 */

/**
 * Propriedades do componente Tab.Panel.
 * @typedef {Object} TabPanelProps
 * @property {ReactNode} children - Conteúdo do painel.
 * @property {number} index - Índice do painel.
 * @property {string} [className] - Classe CSS extra.
 * @property {string} [data-testid] - Test id para testes.
 */

// Contexto para internacionalização de mensagens
export interface TabI18nContext {
  getTabLabel?: (index: number, label?: string) => string;
  getTabTooltip?: (index: number, tooltip?: string) => string;
}
export const TabMessagesContext = createContext<TabI18nContext>({
  getTabLabel: (i, label) => label ?? `Aba ${i + 1}`,
  getTabTooltip: (_i, tooltip) => tooltip ?? "",
});

type TabContextType = {
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  theme: "light" | "dark";
  tabCount: number;
  tabMeta: Array<{ disabled?: boolean }>;
  tabIds: Array<{ tabId: string; panelId: string }>;

};
const TabContext = createContext<TabContextType | undefined>(undefined);

// (logo após hasDisplayName ou antes dos subcomponentes)
function isTabList(
  node: React.ReactNode
): node is React.ReactElement<{ children?: React.ReactNode }> {
  function hasDisplayName(type: unknown): type is { displayName: string } {
    return typeof type === "object" && type !== null && "displayName" in type && typeof (type as { displayName: unknown }).displayName === "string";
  }
  return (
    React.isValidElement(node) &&
    (
      node.type === TabList ||
      (hasDisplayName(node.type) && node.type.displayName === "TabList")
    )
  );
}
// Novo: type guard para itens com possível prop disabled
function isTabItemWithDisabled(
  node: React.ReactNode
): node is React.ReactElement<{ disabled?: boolean }> {
  return React.isValidElement(node) && typeof node.props === "object" && node.props !== null;
}

type TabData = {
  label?: string;
  tooltip?: string;
  disabled?: boolean;
  [key: string]: unknown;
};

type TabProps = {
  children?: ReactNode;
  tabs?: TabData[];
  renderTabItem?: (tab: TabData, index: number, tabId: string, panelId: string) => ReactNode;
  activeIndex?: number;
  onChange?: (index: number) => void;
  defaultIndex?: number;
  theme?: "light" | "dark";
  className?: string;
  "data-testid"?: string;
  i18nMessages?: TabI18nContext;
} & Omit<React.HTMLAttributes<HTMLDivElement>, "onChange">;

// ====== Subcomponentes definidos antes de Tab para permitir referência direta ======
type TabListProps = {
  children: ReactNode;
  className?: string;
  "data-testid"?: string;
  style?: React.CSSProperties;
};
function getNextEnabledTab(
  tabs: Array<{ disabled?: boolean }>,
  start: number,
  dir: 1 | -1
): number {
  let idx = start;
  do {
    idx = (idx + dir + tabs.length) % tabs.length;
  } while (tabs[idx].disabled && idx !== start);
  return idx;
}
function getFirstEnabledTab(tabs: Array<{ disabled?: boolean }>): number {
  for (let i = 0; i < tabs.length; i++) if (!tabs[i].disabled) return i;
  return 0;
}
function getLastEnabledTab(tabs: Array<{ disabled?: boolean }>): number {
  for (let i = tabs.length - 1; i >= 0; i--) if (!tabs[i].disabled) return i;
  return tabs.length - 1;
}
const TabList = React.memo(function TabList({ children, className, "data-testid": dataTestId, style }: TabListProps) {
  const ctx = useContext(TabContext);
  if (!ctx) throw new Error("TabList must be used within a Tab");
  const { theme } = ctx;
  return (
    <div role="presentation">
      <div
        className={clsx(
          "flex border-b-2 border-t-0 border-l-0 border-r-0 mb-4 overflow-x-auto scrollbar-thin scrollbar-thumb-rounded scrollbar-thumb-gray-300",
          theme === "dark" ? "border-gray-700 bg-gray-900" : "border-gray-200 bg-gray-50",
          className
        )}
        role="tablist"
        data-testid={dataTestId ?? "tab-list"}
        style={style}
      >
        {children}
      </div>
    </div>
  );
});

type TabItemProps = {
  children: ReactNode;
  index: number;
  label?: string;
  tooltip?: string;
  disabled?: boolean;
  className?: string;
  "data-testid"?: string;
  "aria-label"?: string;
  tabId?: string;
  panelId?: string;
};
type TabPanelProps = {
  children: ReactNode;
  index: number;
  className?: string;
  "data-testid"?: string;
  tabId?: string;
  panelId?: string;
};

const TabItem = React.memo(function TabItem({
  children,
  index,
  label,
  tooltip,
  disabled = false,
  className,
  "data-testid": dataTestId,
  "aria-label": ariaLabel,
  tabId,
  panelId,
}: TabItemProps) {
  const ctx = useContext(TabContext);
  const i18n = useContext(TabMessagesContext);
  if (!ctx) throw new Error("TabItem must be used within a Tab");
  const { activeIndex, setActiveIndex, theme, tabMeta, tabIds } = ctx;
  const isActive = activeIndex === index;
  const derivedIds = tabIds[index] ?? { tabId, panelId };
  const effectiveTabId = tabId ?? derivedIds.tabId;
  const effectivePanelId = panelId ?? derivedIds.panelId;
  const btnRef = useRef<HTMLButtonElement>(null);
  const lastEventType = useRef<"keyboard" | "mouse" | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null); // Adicionado

  useEffect(() => {
    if (isActive && lastEventType.current === "keyboard") {
      // Foca o wrapper, não o botão
      wrapperRef.current?.focus();
    }
  }, [isActive]);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      lastEventType.current = "keyboard";
      if (disabled) return;
      let nextIdx = index;
      if (e.key === "ArrowRight") {
        nextIdx = getNextEnabledTab(tabMeta, index, 1);
        setActiveIndex(nextIdx);
        e.preventDefault();
      } else if (e.key === "ArrowLeft") {
        nextIdx = getNextEnabledTab(tabMeta, index, -1);
        setActiveIndex(nextIdx);
        e.preventDefault();
      } else if (e.key === "Home") {
        nextIdx = getFirstEnabledTab(tabMeta);
        setActiveIndex(nextIdx);
        e.preventDefault();
      } else if (e.key === "End") {
        nextIdx = getLastEnabledTab(tabMeta);
        setActiveIndex(nextIdx);
        e.preventDefault();
      }
    },
    [disabled, setActiveIndex, index, tabMeta]
  );
  const handleClick = React.useCallback(() => {
    lastEventType.current = "mouse";
    if (!disabled) setActiveIndex(index);
  }, [disabled, setActiveIndex, index]);

  return (
    <div
      ref={wrapperRef} // Adicionado
      role="tab"
      aria-selected={isActive}
      aria-controls={effectivePanelId}
      aria-disabled={disabled}
      aria-label={ariaLabel ?? i18n.getTabLabel?.(index, label)}
      id={effectiveTabId}
      data-testid={dataTestId ?? `tab-item-${index}`}
      tabIndex={disabled ? -1 : isActive ? 0 : -1}
      onKeyDown={handleKeyDown}
      onClick={handleClick}
      className={clsx(
        "relative inline-flex items-center cursor-pointer outline-none",
        disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
      )}
    >
      <Button
        ref={btnRef}
        type="button"
        noBorder
        transparent
        size="medium"
        theme={theme}
        disabled={disabled}
        aria-hidden="true"
        tabIndex={-1}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        data-testid={`${dataTestId ?? `tab-item-${index}`}-button`}
        tooltip={tooltip ? i18n.getTabTooltip?.(index, tooltip) : undefined}
        noHover
        noFocus
        className={clsx(
          "relative appearance-none border-0 outline-none px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base transition-colors duration-200 whitespace-nowrap bg-transparent",
          theme === "dark" ? "text-gray-300" : "text-gray-600",
          isActive
            ? theme === "dark"
              ? "text-blue-400"
              : "text-blue-600"
            : "",
          className
        )}
        style={{ background: "none" }}
      >
        <span className="relative z-10">{children}</span>
        <span
          className={clsx(
            "absolute left-0 right-0 bottom-0 h-0.5 transition-all duration-300",
            isActive
              ? theme === "dark"
                ? "bg-blue-400 scale-x-100"
                : "bg-blue-600 scale-x-100"
              : "bg-transparent scale-x-0"
          )}
          aria-hidden="true"
        />
      </Button>
      <span className="sr-only">
        {ariaLabel ?? i18n.getTabLabel?.(index, label)}
      </span>
    </div>
  );
});

const TabPanel = React.memo(function TabPanel({
  children,
  index,
  className,
  "data-testid": dataTestId,
  tabId,
  panelId,
}: TabPanelProps) {
  const ctx = useContext(TabContext);
  if (!ctx) throw new Error("TabPanel must be used within a Tab");
  const { theme, activeIndex, tabIds } = ctx;
  const derived = tabIds[index] ?? { tabId, panelId };
  const effectiveTabId = tabId ?? derived.tabId;
  const effectivePanelId = panelId ?? derived.panelId;
  const isActive = activeIndex === index;
  return (
    <div
      className={clsx(
        "py-4 px-2 sm:px-0",
        theme === "dark" ? "bg-gray-900 text-gray-100" : "bg-white text-gray-900",
        className
      )}
      id={effectivePanelId}
      role="tabpanel"
      aria-labelledby={effectiveTabId}
      tabIndex={isActive ? 0 : -1}
      aria-hidden={!isActive}
      data-testid={dataTestId ?? `tab-panel-${index}`}
      hidden={!isActive}
    >
      {children}
    </div>
  );
});

TabList.displayName = "TabList";
TabItem.displayName = "TabItem";
TabPanel.displayName = "TabPanel";
// ====== Fim subcomponentes antecipados ======

export function Tab({
  children,
  tabs,
  renderTabItem,
  activeIndex: controlledIndex,
  onChange,
  defaultIndex = 0,
  theme = "light",
  className,
  "data-testid": dataTestId,
  i18nMessages,
  ...divProps
}: TabProps) {
  const [internalIndex, setInternalIndex] = useState(defaultIndex);
  const isControlled = controlledIndex !== undefined;

  const tabCount = useMemo(() => {
    if (tabs) return tabs.length;
    let count = 0;
    React.Children.forEach(children, child => {
      if (isTabList(child)) {
        React.Children.forEach(child.props.children, ti => {
          if (isTabItemWithDisabled(ti)) count++;
        });
      }
    });
    return count;
  }, [children, tabs]);

  let activeIndex = isControlled ? controlledIndex! : internalIndex;
  if (activeIndex > tabCount - 1) activeIndex = tabCount > 0 ? tabCount - 1 : 0;

  const setActiveIndex = React.useCallback(
    (index: number) => {
      if (!isControlled) setInternalIndex(index);
      onChange?.(index);
    },
    [isControlled, onChange]
  );

  const idBase = useId();
  const tabIds = useMemo(
    () =>
      Array.from({ length: tabCount }, (_, i) => ({
        tabId: `tab-${i}-${idBase}`,
        panelId: `tabpanel-${i}-${idBase}`,
      })),
    [tabCount, idBase]
  );

  const defaultI18nMessages = useContext(TabMessagesContext);
  const tabContextValue = useMemo(
    () => ({
      activeIndex,
      setActiveIndex,
      theme,
      tabCount,
      tabMeta: tabs
        ? tabs.map(t => ({ disabled: t.disabled }))
        : (() => {
            const meta: Array<{ disabled?: boolean }> = [];
            React.Children.forEach(children, child => {
              if (isTabList(child)) {
                React.Children.forEach(child.props.children, ti => {
                  if (isTabItemWithDisabled(ti)) meta.push({ disabled: !!ti.props.disabled });
                });
              }
            });
            return meta;
          })(),
      tabIds,
    }),
    [activeIndex, setActiveIndex, theme, tabCount, tabs, children, tabIds]
  );

  return (
    <TabMessagesContext.Provider value={i18nMessages || defaultI18nMessages}>
      <TabContext.Provider value={tabContextValue}>
        <div
          className={clsx(
            "w-full",
            theme === "dark" ? "bg-gray-900 text-gray-100" : "bg-white text-gray-900",
            className
          )}
          data-testid={dataTestId ?? "tab"}
          {...divProps}
        >
          {tabs && renderTabItem ? (
            <>
              <TabList>
                {tabs.map((tab, idx) =>
                  renderTabItem(tab, idx, tabIds[idx].tabId, tabIds[idx].panelId)
                )}
              </TabList>
              {children}
            </>
          ) : (
            children
          )}
        </div>
      </TabContext.Provider>
    </TabMessagesContext.Provider>
  );
}

// Subcomponentes
Tab.List = TabList;
Tab.Item = TabItem;
Tab.Panel = TabPanel;

// Tipagem dos subcomponentes para melhor autocomplete
export interface TabComponent extends React.FC<TabProps> {
  List: typeof TabList;
  Item: typeof TabItem;
  Panel: typeof TabPanel;
}
const TabTyped = Tab as TabComponent;
TabTyped.List = TabList;
TabTyped.Item = TabItem;
TabTyped.Panel = TabPanel;

export default TabTyped;